/**
 * Resizes and compresses an image file while maintaining quality
 * @param {File} file - The original image file
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width in pixels (default: 800)
 * @param {number} options.maxHeight - Maximum height in pixels (default: 800)
 * @param {number} options.quality - Image quality 0-1 (default: 0.9 for 90%)
 * @param {string} options.outputType - Output mime type (default: 'image/jpeg')
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.9,
    outputType = 'image/jpeg'
  } = options

  return new Promise((resolve, reject) => {
    // Create file reader to read the image
    const reader = new FileReader()

    reader.onload = (event) => {
      const img = new Image()

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img

        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height

          if (width > height) {
            width = maxWidth
            height = Math.round(maxWidth / aspectRatio)
          } else {
            height = maxHeight
            width = Math.round(maxHeight * aspectRatio)
          }
        }

        // Create canvas for resizing
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')

        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        // Draw the image on canvas
        ctx.drawImage(img, 0, 0, width, height)

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'))
              return
            }

            // Determine file extension based on output type
            const extension = outputType === 'image/png' ? 'png' : 'jpg'
            const fileName = file.name.replace(/\.[^/.]+$/, '') + '_compressed.' + extension

            // Create new File from blob
            const compressedFile = new File([blob], fileName, {
              type: outputType,
              lastModified: Date.now()
            })

            // Log compression stats
            const originalSize = (file.size / 1024).toFixed(2)
            const compressedSize = (compressedFile.size / 1024).toFixed(2)
            const savings = (((file.size - compressedFile.size) / file.size) * 100).toFixed(1)
            console.log(`Image compressed: ${originalSize}KB -> ${compressedSize}KB (${savings}% reduction)`)

            resolve(compressedFile)
          },
          outputType,
          quality
        )
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      img.src = event.target.result
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Compresses image specifically for logo upload
 * Uses optimized settings for logo images
 * @param {File} file - The original image file
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressLogoImage(file) {
  const isPng = file.type === 'image/png'
  return compressImage(file, {
    maxWidth: 500,
    maxHeight: 500,
    quality: 0.9,
    outputType: isPng ? 'image/png' : 'image/jpeg',
  })
}

/**
 * Compresses image for general use with custom dimensions
 * @param {File} file - The original image file
 * @param {number} maxDimension - Maximum width or height
 * @param {number} quality - Quality 0-1 (default 0.9)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImageWithDimension(file, maxDimension = 1200, quality = 0.9) {
  return compressImage(file, {
    maxWidth: maxDimension,
    maxHeight: maxDimension,
    quality,
    outputType: 'image/jpeg'
  })
}
